import { useMemo, useState } from 'react'

import { showToast } from '../../../utils/toast'
import { useFermaRun, useRun } from './api'
import {
  corpoRipeti,
  corpoRipetiFalliti,
  costoRun,
  descriviCosto,
  divisiPerEsito,
  filtraRisultati,
  formattaCosto,
  nomeFlusso,
  nomeRun,
  nomeVoceCosto,
  passiRun,
  percentualeAvanzamento,
  quando,
  riassuntoRun,
  runAttiva,
  totaliPerValuta,
  varianteStato
} from './Functions'
import { Icona } from './Icon'
import { RisultatoScenario } from './ScenarioResult'
import { t } from './testi'

/** Righe grigie al posto del contenuto mentre la run si carica. */
const Scheletro = () => (
  <div className="ae-skel-gruppo" role="status" aria-label={t.aeCaricamentoRun}>
    <span className="ae-skel ae-skel--titolo" />
    <span className="ae-skel ae-skel--riga" />
    <span className="ae-skel ae-skel--riga" />
    <span className="ae-skel ae-skel--blocco" />
  </div>
)

/** Un contatore della sintesi: numero grande, etichetta sotto. */
const Contatore = ({ valore, etichetta, tono }) => (
  <div className={`ae-contatore ${tono ? `is-${tono}` : ''}`}>
    <strong>{valore}</strong>
    <span>{etichetta}</span>
  </div>
)

/** Una sezione di scenari (sotto soglia, sopra, saltati) con i suoi risultati. */
const Sezione = ({ titolo, risultati, tono, aperto, epoca }) =>
  risultati.length ? (
    <section className={`ae-macro ${tono ? `ae-macro--${tono}` : ''}`}>
      <h4 className="ae-macro__titolo">
        {titolo} <span className="ae-macro__numero">{risultati.length}</span>
      </h4>
      {risultati.map((r) => (
        <RisultatoScenario key={`${r.id}-${epoca}`} r={r} aperto={aperto} />
      ))}
    </section>
  ) : null

/** Le azioni sulla run: ferma se è viva, ripeti e ripeti i falliti altrimenti. */
const Azioni = ({ run, onRipeti }) => {
  const ferma = useFermaRun()
  const [fermando, setFermando] = useState(false)
  const [ripetendo, setRipetendo] = useState(false)
  const falliti = corpoRipetiFalliti(run)
  const corpo = corpoRipeti(run)

  const ripeti = async (richiesta) => {
    setRipetendo(true)
    try {
      await onRipeti(richiesta)
    } catch {
      /* il toast d'errore l'ha già mostrato il chiamante */
    } finally {
      setRipetendo(false)
    }
  }

  return (
    <div className="ae-azioni">
      {runAttiva(run) ? (
        <button
          type="button"
          className="nbtn nbtn--ghost nbtn--sm"
          disabled={fermando}
          onClick={async () => {
            setFermando(true)
            try {
              await ferma(run.id)
            } catch (errore) {
              showToast('error', t.aeFermaFallito, errore?.message)
            } finally {
              setFermando(false)
            }
          }}
        >
          <Icona nome="pi-stop-circle" /> {t.aeFerma}
        </button>
      ) : null}
      {falliti ? (
        <button
          type="button"
          className="nbtn nbtn--primary nbtn--sm"
          disabled={ripetendo}
          onClick={() => ripeti(falliti)}
        >
          <Icona nome="pi-replay" /> {t.aeRipetiFalliti} ({falliti.scenari.length})
        </button>
      ) : null}
      {corpo ? (
        <button
          type="button"
          className="nbtn nbtn--ghost nbtn--sm"
          disabled={ripetendo}
          onClick={() => ripeti(corpo)}
        >
          <Icona nome="pi-refresh" /> {t.aeRipeti}
        </button>
      ) : null}
    </div>
  )
}

/** Il costo della run: totale e ripartizione per modello/provider. */
const CostoRun = ({ costo }) =>
  costo.length ? (
    <details className="ae-costo-dettaglio">
      <summary>
        <span className="ae-muto">{t.aeCosto}</span>{' '}
        <strong>{descriviCosto(totaliPerValuta(costo))}</strong>
      </summary>
      <ul className="ae-costo-voci">
        {costo.map((v) => (
          <li key={`${v.valuta}-${v.voce ?? ''}`}>
            <span>{nomeVoceCosto(v.voce) || t.aeCosto}</span>
            <span>{formattaCosto(v.totale, v.valuta)}</span>
          </li>
        ))}
      </ul>
    </details>
  ) : null

/**
 * Il dettaglio di una run: titolo e stato, azioni, sintesi in tre contatori, poi gli scenari
 * (prima i rossi) con ricerca e filtro «solo da rivedere».
 * @param {Object} props - Proprietà
 * @param {string} props.id - La run scelta
 * @param {(corpo: Object) => Promise<void>} props.onRipeti - Lancia una run nuova
 */
export const DettaglioRun = ({ id, onRipeti }) => {
  const { data: run, isLoading, isError, error, refetch, isFetching } = useRun(id)
  const [testo, setTesto] = useState('')
  const [soloDaRivedere, setSoloDaRivedere] = useState(false)
  // Cambia a «apri tutti»/«chiudi tutti»: rimonta gli scenari con l'apertura voluta.
  const [forza, setForza] = useState({ aperto: undefined, epoca: 0 })

  const risultati = useMemo(
    () => filtraRisultati(run?.risultati ?? [], { testo, soloDaRivedere }),
    [run, testo, soloDaRivedere]
  )

  if (isLoading) return <Scheletro />
  if (isError || !run)
    return (
      <div className="ae-stato-vuoto" role="alert">
        <p>{t.aeRunIllegibile}</p>
        {error?.message ? <p className="ae-muto">{error.message}</p> : null}
        <button type="button" className="nbtn nbtn--ghost nbtn--sm" onClick={() => refetch()}>
          <Icona nome="pi-refresh" /> {t.aeRiprova}
        </button>
      </div>
    )

  const { verdi, eseguiti } = riassuntoRun(run)
  const passi = passiRun(run)
  const costo = costoRun(run)
  const attiva = runAttiva(run)
  const percentuale = percentualeAvanzamento(run)
  const { rossi, verdi: sopra, saltati } = divisiPerEsito(risultati)
  const totali = divisiPerEsito(run.risultati ?? [])
  const molti = (run.risultati ?? []).length > 6

  return (
    <>
      <header className="ae-dettaglio__testa">
        <div className="ae-dettaglio__titolo">
          <h3 className="ncard__title">{nomeRun(run) || run.id}</h3>
          <div className="ae-dettaglio__stato">
            <span className={`nbadge ${varianteStato(run.stato)}`}>
              {{
                in_corso: t.aeInCorso,
                in_coda: t.aeInCoda,
                completata: t.aeCompletata,
                fermata: t.aeFermata,
                fallita: t.aeFallita
              }[run.stato] ?? run.stato}
            </span>
            <span className="ae-muto">{quando(run.avviataIl)}</span>
            {isFetching && attiva ? <Icona nome="pi-spin pi-spinner" /> : null}
          </div>
        </div>
        <Azioni run={run} onRipeti={onRipeti} />
      </header>

      {attiva ? (
        <div className="ae-avanzamento" aria-live="polite">
          <div className="ae-avanzamento__riga">
            <span>
              {t.aeAvanzamento}: {passi.fatti + (passi.saltati ?? 0)}/{passi.totali}
            </span>
            <strong>{percentuale}%</strong>
          </div>
          <span
            className="ae-barra-passi ae-barra-passi--grande"
            role="progressbar"
            aria-label={t.aeAvanzamento}
            aria-valuenow={percentuale}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <span style={{ width: `${percentuale}%` }} />
          </span>
        </div>
      ) : null}

      {run.errore ? (
        <p className="ae-errore" role="alert">
          {run.errore}
        </p>
      ) : null}

      <div className="ae-contatori">
        <Contatore
          valore={totali.rossi.length}
          etichetta={t.aeSottoSoglia}
          tono={totali.rossi.length ? 'ko' : undefined}
        />
        <Contatore valore={totali.verdi.length} etichetta={t.aeSopraSogliaTitolo} tono="ok" />
        <Contatore valore={totali.saltati.length} etichetta={t.aeSaltatiTitolo} />
        <Contatore valore={`${verdi}/${eseguiti}`} etichetta={t.aeSopraSoglia} />
      </div>

      <dl className="ae-meta">
        <div>
          <dt>{t.aeEnvironment}</dt>
          <dd>{run.environmentId}</dd>
        </div>
        <div>
          <dt>{t.aeFlusso}</dt>
          <dd>{nomeFlusso(run.contratto?.mode)}</dd>
        </div>
        <div>
          <dt>{t.aeRicerca}</dt>
          <dd>{run.ricerca}</dd>
        </div>
        <div>
          <dt>{t.aeHarness}</dt>
          <dd>{run.versioneHarness ? `v${run.versioneHarness}` : t.aeSenzaVersione}</dd>
        </div>
        <div className="ae-meta__largo">
          <dt>{t.aeModello}</dt>
          <dd>{run.modello}</dd>
        </div>
        {run.campagna ? (
          <div className="ae-meta__largo">
            <dt>{t.aeCampagna}</dt>
            <dd>
              «{run.campagna.nome}» · env {run.campagna.environmentIds.join(', ')}
            </dd>
          </div>
        ) : null}
      </dl>
      <CostoRun costo={costo} />
      <details className="ae-tecnici">
        <summary className="ae-muto">{t.aeFirmaCommit}</summary>
        <code>
          {t.aeFirma} {run.firmaHarness} · commit {run.commit}
        </code>
      </details>

      {molti ? (
        <div className="ae-barra-risultati">
          <input
            className="ninput"
            type="search"
            placeholder={t.aeCercaScenario}
            aria-label={t.aeCercaScenario}
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
          />
          <button
            type="button"
            className={`nbtn nbtn--sm ${soloDaRivedere ? 'nbtn--primary' : 'nbtn--ghost'}`}
            aria-pressed={soloDaRivedere}
            onClick={() => setSoloDaRivedere((v) => !v)}
          >
            {t.aeSoloDaRivedere}
          </button>
          <button
            type="button"
            className="nbtn nbtn--ghost nbtn--sm"
            onClick={() => setForza((f) => ({ aperto: true, epoca: f.epoca + 1 }))}
          >
            {t.aeApriTutti}
          </button>
          <button
            type="button"
            className="nbtn nbtn--ghost nbtn--sm"
            onClick={() => setForza((f) => ({ aperto: false, epoca: f.epoca + 1 }))}
          >
            {t.aeChiudiTutti}
          </button>
        </div>
      ) : null}

      <Sezione
        titolo={t.aeSottoSoglia}
        risultati={rossi}
        tono="ko"
        aperto={forza.aperto}
        epoca={forza.epoca}
      />
      <Sezione
        titolo={t.aeSopraSogliaTitolo}
        risultati={sopra}
        aperto={forza.aperto ?? false}
        epoca={forza.epoca}
      />
      {saltati.length ? (
        <details className="ae-macro ae-saltati">
          <summary className="ae-macro__titolo">
            {t.aeSaltatiTitolo} <span className="ae-macro__numero">{saltati.length}</span>
          </summary>
          {saltati.map((r) => (
            <RisultatoScenario key={r.id} r={r} />
          ))}
        </details>
      ) : null}
      {!risultati.length ? (
        <p className="ae-muto ae-vuoto">
          {(run.risultati ?? []).length ? t.aeNessunRisultato : t.aeCaricamentoRun}
        </p>
      ) : null}
    </>
  )
}
